import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faFloppyDisk, faPencil } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { PAGE_PATHS } from './base_report';
import { Button } from 'components/ui/button';
import Dropdown from 'components/dropdown';
import LoadingSpinner from 'components/ui/loading_spinner';
import PropTypes from 'prop-types';
import { showSnackbar } from 'components/ui/snackbar/helper';

export const GENERATING_CSV_STEPS = {
  DEFAULT: 'default',
  GENERATING: 'generating',
  GENERATED: 'generated',
};

export default function ReportHeaderActions({
  allSelected,
  csvDownloadMessage,
  csvDownloadPath,
  csvIgnoresSelection = false,
  disablePdf = false,
  downloadKey,
  generatingCSVStep,
  hideColumnCustomization = false,
  hideSavedReports = false,
  ransackParams,
  savedReport,
  selectedColumns,
  selectedRecordIds,
  timeZone,
  totalCount,
  onEditColumns,
  onPathChanged,
  onSaveAs,
  onUpdateReport
}) {
  const csvReturnFields = () => selectedColumns.map(column => column.label);
  // With PDF off and columns hidden, Actions would open on nothing.
  const hasActions = !disablePdf || !hideColumnCustomization;
  // Every row the filters return, rather than only the ticked ones.
  const csvOfFilteredRows = csvIgnoresSelection || allSelected || selectedRecordIds.length === 0;

  function startCsvDownload() {
    if (generatingCSVStep !== GENERATING_CSV_STEPS.DEFAULT) return;

    onPathChanged(PAGE_PATHS.GENERATE_CSV);

    const rowCount = csvOfFilteredRows ? totalCount : selectedRecordIds.length;
    if (csvDownloadMessage && Number.isFinite(rowCount)) {
      showSnackbar({ text: csvDownloadMessage({ columnCount: selectedColumns.length, rowCount }), variant: 'info' });
    }
  }

  function createCsvPath() {
    const url = new URL(csvDownloadPath, window.location.origin);
    if (csvOfFilteredRows) {
      Object.entries(ransackParams()).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((individualValue) => {
            url.searchParams.append(`${key}[]`, individualValue);
          });
        } else {
          url.searchParams.append(key, value);
        }
      });
    } else {
      selectedRecordIds.forEach(recordId => url.searchParams.append('ids[]', recordId));
    }
    csvReturnFields().forEach(field => url.searchParams.append('columns[]', field));
    if (timeZone && !url.searchParams.has('time_zone')) {
      url.searchParams.append('time_zone', timeZone);
    }
    url.searchParams.append('key', downloadKey.current);
    return url.toString();
  }

  const renderSaveDropdownActions = () => {
    // Only a view's creator can save over it; anyone else saves a copy.
    if (!savedReport?.owned) {
      return (
        <div>
          <Button icon={faFloppyDisk} label="Save As" onClick={onSaveAs} />
        </div>
      );
    } else {
      return (
        <Dropdown icon={faFloppyDisk} id="save-dropdown" label="Save" variant="primary">
          <Dropdown.Item
            key="save-as-report"
            name="save-as-report"
            onClick={onSaveAs}
          >
            Save As
          </Dropdown.Item>
          <Dropdown.Item
            key="save-report"
            onClick={onUpdateReport}
          >
            Save
          </Dropdown.Item>
        </Dropdown>
      );
    }
  };

  return (
    <div className="d-flex gap-3">
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATING && (
        <div className="text-primary d-flex align-items-center gap-2">
          <LoadingSpinner flex size="md" skipTopBuffer />
          Generating CSV...
        </div>
      )}
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATED && (
        <div className="text-success d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCheck}/>
          Generated CSV
        </div>
      )}
      {csvDownloadPath && (
        <Button
          aria-label="Download CSV"
          className="px-2"
          disabled={generatingCSVStep !== GENERATING_CSV_STEPS.DEFAULT}
          href={generatingCSVStep === GENERATING_CSV_STEPS.DEFAULT ? createCsvPath() : ''}
          icon={faDownload}
          variant="ghost"
          onClick={startCsvDownload}
        />
      )}
      {hasActions && (
        <Dropdown id="actions-dropdown" label="Actions">
          {!disablePdf && (
            <Dropdown.Item
              disabled={selectedRecordIds.length === 0}
              key="generate-pdf"
              onClick={() => onPathChanged(PAGE_PATHS.GENERATE_PDF)}
            >
              <FontAwesomeIcon className="mr-2" fixedWidth icon={faDownload}/>
              Download PDF
            </Dropdown.Item>
          )}
          {!hideColumnCustomization && (
            <Dropdown.Item key="edit-columns" onClick={onEditColumns}>
              <FontAwesomeIcon className="mr-2" fixedWidth icon={faPencil}/>
              Edit Columns
            </Dropdown.Item>
          )}
        </Dropdown>
      )}
      {!hideSavedReports && renderSaveDropdownActions()}
    </div>
  );
}

ReportHeaderActions.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  // Given { columnCount, rowCount }, the message shown when a download starts. Omit for none.
  csvDownloadMessage: PropTypes.func,
  csvDownloadPath: PropTypes.string,
  // Download every row the filters return even while rows are ticked.
  csvIgnoresSelection: PropTypes.bool,
  disablePdf: PropTypes.bool,
  downloadKey: PropTypes.object.isRequired,
  generatingCSVStep: PropTypes.oneOf(Object.values(GENERATING_CSV_STEPS)).isRequired,
  hideColumnCustomization: PropTypes.bool,
  hideSavedReports: PropTypes.bool,
  ransackParams: PropTypes.func.isRequired,
  savedReport: PropTypes.object,
  selectedColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRecordIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  timeZone: PropTypes.string,
  // Every row the filters return, for the download message.
  totalCount: PropTypes.number,
  onEditColumns: PropTypes.func.isRequired,
  onPathChanged: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  onUpdateReport: PropTypes.func.isRequired,
};
